import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { getOrderRejection } from "../../../api/bseApi";

/**
 * Parse BSE Order Rejection response (pipe-separated)
 * Expected: OrderNo|OrderType|RejectionCode|RejectionReason|RejectionStage|Remarks
 */
function parseOrderRejectionResponse(rawData) {
  if (!rawData || typeof rawData !== "string") return null;
  const trimmed = rawData.trim();
  if (!trimmed) return null;
  const parts = trimmed.split("|").map((p) => (p || "").trim());
  return {
    orderNo: parts[0] || "-",
    orderType: parts[1] || "Purchase",
    rejectionCode: parts[2] || "",
    rejectionReason: parts[3] || "",
    rejectionStage: parts[4] || "",
    remarks: parts[5] || parts[4] || parts[3] || "",
  };
}

/**
 * Detect if rejection is potentially correctable (mandate, FATCA, etc.)
 */
function isCorrectable(rejectionReason, remarks) {
  const text = `${(rejectionReason || "").toLowerCase()} ${(remarks || "").toLowerCase()}`;
  const correctable = ["mandate", "fatca", "pan", "kyc", "bank", "document", "incomplete", "missing", "register"];
  const nonCorrectable = ["scheme closed", "cut-off", "cut off", "cutoff", "expired", "time limit", "invalid scheme"];
  if (nonCorrectable.some((k) => text.includes(k))) return false;
  return correctable.some((k) => text.includes(k));
}

/**
 * OrderRejectionScreen - BSE StAR MF Order Rejection (MFAPI Flag 12)
 * Opens automatically when Order Status = REJECTED/FAILED, or manually from Order Status
 * Receives: OrderNo (mandatory), ClientCode (mandatory)
 */
export default function OrderRejectionScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();
  const state = location.state;
  const [context, setContext] = useState(null);
  const [rejection, setRejection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fetching, setFetching] = useState(true);

  const clientCode = state?.clientCode || new URLSearchParams(location.search).get("clientCode");
  const orderNo = state?.orderNo || new URLSearchParams(location.search).get("orderNo");

  const fetchRejection = useCallback(async () => {
    if (!context?.orderNo || !context?.clientCode) return null;
    try {
      const res = await getOrderRejection({
        orderNo: context.orderNo,
        clientCode: context.clientCode,
      });
      if (res.data?.status === 1) {
        const parsed = parseOrderRejectionResponse(res.data?.data);
        if (parsed) return parsed;
        return { orderNo: context?.orderNo || "-", orderType: "", rejectionCode: "", rejectionReason: "No additional rejection details available.", rejectionStage: "", remarks: "" };
      }
      return null;
    } catch (err) {
      console.warn("Order rejection fetch failed:", err);
      return null;
    }
  }, [context?.orderNo, context?.clientCode]);

  useEffect(() => {
    const o = (orderNo || "").toString().trim();
    const c = (clientCode || "").toString().trim();
    if (o && c) {
      setContext({ orderNo: o, clientCode: c });
      setFetching(false);
      return;
    }
    if (userId && o && !c) {
      const loadUser = async () => {
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:8000/api"}/user/${userId}`
          );
          const user = res.data?.status === 1 ? res.data?.data : null;
          if (!user) {
            Swal.fire("Error", "User not found.", "error").then(() => navigate("/user"));
            return;
          }
          const ucc = user.ucc ?? user.UCC ?? "";
          if (!ucc || String(ucc).trim() === "" || String(ucc) === "Reject") {
            Swal.fire("Error", "ClientCode (UCC) not available.", "error").then(() => navigate("/user"));
            return;
          }
          setContext({ orderNo: o, clientCode: String(ucc).trim() });
        } catch (err) {
          Swal.fire("Error", err.response?.data?.error || "Failed to load user.", "error").then(() => navigate("/user"));
        } finally {
          setFetching(false);
        }
      };
      loadUser();
      return;
    }
    setError("OrderNo and ClientCode are required. Open from Order Status.");
    setFetching(false);
  }, [orderNo, clientCode, userId, navigate]);

  useEffect(() => {
    if (!context?.orderNo || !context?.clientCode) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      const result = await fetchRejection();
      setLoading(false);
      setRejection(result);
      if (!result) {
        setError("Could not fetch order rejection details.");
      }
    };

    load();
  }, [context?.orderNo, context?.clientCode, fetchRejection]);

  const handleRefresh = async () => {
    if (!context) return;
    setLoading(true);
    const result = await fetchRejection();
    setLoading(false);
    setRejection(result);
  };

  if (fetching || (!context && !error)) {
    return (
      <div className="main-content">
        <div className="page-wrapper page-settings">
          <div className="content p-4">
            <div className="text-center">
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              Loading...
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !context) {
    return (
      <div className="main-content">
        <div className="page-wrapper page-settings">
          <div className="content p-4">
            <div className="text-center">
              <i className="fas fa-exclamation-triangle text-warning fa-3x mb-3" />
              <h5>OrderNo and ClientCode Required</h5>
              <p className="text-muted mb-3">{error}</p>
              <Link to="/user/order-status" className="btn btn-primary">Back to Order Status</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const correctable = rejection ? isCorrectable(rejection.rejectionReason, rejection.remarks) : false;

  return (
    <div className="main-content">
      <div className="page-wrapper page-settings">
        <div className="content">
          <div
            className="mb-4"
            style={{
              background: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              padding: "20px",
            }}
          >
            <nav aria-label="breadcrumb" style={{ marginBottom: "15px" }}>
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li>
                <li className="breadcrumb-item"><Link to="/user">Users</Link></li>
                <li className="breadcrumb-item"><Link to="/user/order-status" state={{ clientCode: context?.clientCode }}>Order Status</Link></li>
                <li className="breadcrumb-item active">Order Rejection</li>
              </ol>
            </nav>
            <h5>Order Rejection Details</h5>
            <p className="text-muted mb-0">
              Order: <strong>{context?.orderNo}</strong> | Client: <strong>{context?.clientCode}</strong>
            </p>
          </div>

          <div
            className="mb-4"
            style={{
              background: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              padding: "24px",
              border: "1px solid #e6eef0",
            }}
          >
            <nav className="d-flex justify-content-between align-items-center mb-4">
              <h6 className="mb-0" style={{ color: "#0b6f68", fontWeight: "600" }}>
                Rejection Details
              </h6>
              <button className="btn btn-outline-primary btn-sm" onClick={handleRefresh} disabled={loading}>
                {loading ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="fas fa-sync-alt me-1" />}
                Refresh
              </button>
            </nav>

            {loading && !rejection && (
              <div className="text-center py-5">
                <span className="spinner-border spinner-border-lg text-primary mb-3" role="status" />
                <p>Fetching order rejection details...</p>
              </div>
            )}

            {error && !rejection && !loading && (
              <div className="alert alert-warning">
                <i className="fas fa-info-circle me-2" />
                {error}
                <div className="mt-3">
                  <button className="btn btn-outline-primary btn-sm me-2" onClick={handleRefresh}>Retry</button>
                  <Link to="/user/order-status" state={{ clientCode: context?.clientCode }} className="btn btn-outline-secondary btn-sm">Back to Order Status</Link>
                </div>
              </div>
            )}

            {rejection && (
              <>
                <div className="alert alert-danger border-0 mb-4" style={{ background: "#fee2e2" }}>
                  <h6 className="mb-3"><i className="fas fa-times-circle me-2" />Order Rejected</h6>
                  <dl className="row mb-0">
                    <dt className="col-sm-3">Order Number</dt>
                    <dd className="col-sm-9">{rejection.orderNo}</dd>

                    <dt className="col-sm-3">Order Type</dt>
                    <dd className="col-sm-9">{rejection.orderType}</dd>

                    {rejection.rejectionCode && (
                      <>
                        <dt className="col-sm-3">Rejection Code</dt>
                        <dd className="col-sm-9">{rejection.rejectionCode}</dd>
                      </>
                    )}

                    <dt className="col-sm-3">Rejection Reason</dt>
                    <dd className="col-sm-9 fw-bold">{rejection.rejectionReason || "No additional rejection details available."}</dd>

                    {rejection.rejectionStage && (
                      <>
                        <dt className="col-sm-3">Rejection Stage</dt>
                        <dd className="col-sm-9">{rejection.rejectionStage}</dd>
                      </>
                    )}

                    {(rejection.remarks && rejection.remarks !== rejection.rejectionReason) && (
                      <>
                        <dt className="col-sm-3">Additional Remarks</dt>
                        <dd className="col-sm-9">{rejection.remarks}</dd>
                      </>
                    )}
                  </dl>
                </div>

                <div className="d-flex gap-2 flex-wrap">
                  <Link to="/user/order-status" state={{ clientCode: context?.clientCode }} className="btn btn-outline-primary">
                    Go Back to Order Status
                  </Link>
                  {correctable && (
                    <Link
                      to="/user/order-status"
                      state={{ clientCode: context?.clientCode }}
                      className="btn btn-primary"
                    >
                      <i className="fas fa-wrench me-1" /> Fix & Retry
                    </Link>
                  )}
                  {!correctable && (
                    <span className="btn btn-secondary disabled" title="This rejection cannot be corrected">
                      <i className="fas fa-ban me-1" /> Retry disabled (non-correctable)
                    </span>
                  )}
                </div>
              </>
            )}

            {rejection && (
              <div className="alert alert-light border mt-4">
                <p className="mb-0 small">
                  <strong>Fix & Retry</strong> is shown when the rejection may be correctable (e.g. mandate missing, FATCA incomplete).
                  <strong> Retry disabled</strong> for non-correctable errors (e.g. scheme closed, cut-off crossed).
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
